import { lazy, Suspense } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
  IconLayoutSidebarRight,
} from "@tabler/icons-react";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import FigmaEmbed from "../../../components/FigmaEmbed";
import Loader from "../../../components/Loader";
import { RoomJoinOffer } from "../../../components/room/DiscoverableRoomBlock";
import { DesktopRoomSource } from "../../../components/room/DesktopRoomSource";
import Skeleton from "../../../components/skeleton/Skeleton";
import { Button, buttonVariants } from "../../../components/ui/Button";
import { FullscreenRegion } from "../../../components/ui/FullscreenRegion";
import { PanZoomImage } from "../../../components/ui/PanZoomImage";
import { ViewerToolbarButton } from "../../../components/ui/ViewerToolbar";
import { ZoomableFrame } from "../../../components/ui/ZoomableFrame";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useEffectOnce } from "../../../domains/app/hooks/useEffectOnce";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import {
  formatArticleDate,
  formatShortDate,
} from "../../../domains/app/utils/renderDate";
import { useCongressAttributionFromNavigation } from "../../../domains/congress/congressAttribution";
import { useShareInfographic } from "../../../domains/infographic/hooks/useShareInfographic";
import { infographicSource } from "../../../domains/infographic/infographicSource";
import { useCanOpenRoom } from "../../../domains/room/hooks/useCanOpenRoom";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { useSharedContentBack } from "../../../domains/room/hooks/useSharedContentBack";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { isUserAuthentified } from "../../../domains/user/utils/isUserAuthentified";
import { createLocalStore, createMemoryStore } from "../../../lib/store";
import { cn } from "../../../lib/utils";
import { z } from "zod";
import { apiClient, useApiQuery, type Api } from "../../../services/api";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventPageView } from "../../../tools/analytics/common.analytics";
import { gaEventViewItemListArticle } from "../../../tools/analytics/domains/article.analytics";
import { gaEventViewItemInfographic } from "../../../tools/analytics/domains/infographic.analytics";
import { openExternalUrl } from "../../../tools/open-external-url";
import { useInfographicProgress } from "../useInfographicProgress";

const LazyPdfViewer = lazy(() => import("../../../components/PdfViewer"));

type ContentDetail = Api["get"]["/webapp/public/content/:id"]["output"]["body"];
type InfographicDetail = Extract<
  ContentDetail,
  { content_format: "Infographic" }
>;

// The page turned to, for the PDF on screen: another infographic starts on
// its first page.
const usePdfPaging = createMemoryStore<{
  url: string | null;
  page: number;
  count: number | null;
}>({ url: null, page: 1, count: null });

// Whether the reader keeps the panel beside the viewer, on every
// infographic, from one visit to the next.
const useInfographicPanelOpen = createLocalStore(
  "desktop-infographic-panel-open",
  true,
  z.boolean()
);

/**
 * An infographic on a desktop (Figma "02 - Explore / Vidéo & Infographie",
 * "D3.infographic.default"): a viewer that fits the infographic in the page,
 * zooms it and puts it full screen, and a 360px panel with its title, its
 * room, what the reader can do with it and the related content.
 */
export function DesktopInfographicPage({
  slug,
  roomId,
}: {
  slug: string;
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { contentLang } = useActiveLang();
  const handleBack = useSharedContentBack(roomId);
  const [isPanelOpen, setPanelOpen] = useInfographicPanelOpen();
  // The row only dresses the page (logo, analytics). A shared link brings
  // readers the row refuses (no session, or not in a private room), and the
  // refusal would only reach Sentry: ask only when the reader can open it.
  const canOpenRoom = useCanOpenRoom(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null && canOpenRoom === "open"
      ? { param: { id: roomId } }
      : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  // Stamped by the card that navigated here; null on every other entry.
  const congressAttribution = useCongressAttributionFromNavigation();

  const contentQuery = useApiQuery("/webapp/public/content/slug/:slug", {
    param: { slug },
  });
  const infographic: InfographicDetail | null =
    contentQuery.state === "success" &&
    contentQuery.body.content_format === "Infographic"
      ? contentQuery.body
      : null;
  const infographicProgress = useInfographicProgress({
    infographic,
    surface: "desktop",
  });

  useEffectOnce(() => {
    if (infographic === null) return;
    void (async () => {
      try {
        await gaEventPageView(currentRoom);
        // A shared link opens this page with no session, and the views
        // route is private.
        if (await isUserAuthentified()) {
          await apiClient.post("/webapp/private/content/:id/views", {
            param: { id: infographic.id },
          });
        }
        gaEventViewItemInfographic(
          { infographic, listName: AnalyticsItemList.INFOGRAPHIC_PAGE },
          congressAttribution
        );
        if (infographic.associatedArticles.length > 0) {
          gaEventViewItemListArticle({
            articles: infographic.associatedArticles,
            listName: AnalyticsItemList.INFOGRAPHIC_PAGE,
          });
        }
      } catch {
        // analytics-only, captured by onRequestError
      }
    })();
  }, [infographic === null ? null : infographic.id]);

  const topNav = (
    <DesktopTopNav
      context={
        <DesktopBreadcrumb
          back={{
            kind: "separate",
            onBack: () => {
              infographicProgress.noteExit("back");
              handleBack();
            },
          }}
          // An infographic is a resource of its room: the nearest crumb
          // returns to the room's Resources.
          trail={
            currentRoom === null
              ? [{ label: t("Explore"), to: "/discovery" }]
              : [
                  { label: t("Rooms"), to: "/rooms" },
                  {
                    label: getRoomName(currentRoom, contentLang),
                    to: `/room/resources/${currentRoom.id}`,
                  },
                ]
          }
          current={t("Infographic")}
        />
      }
      // The viewer is the page's workspace, beside its panel: the bar holds
      // the panel toggle, as on a conversation, and the search stays one ⌘K
      // away.
      trailing={{
        kind: "actions",
        content: (
          <button
            type="button"
            onClick={() => {
              setPanelOpen(!isPanelOpen);
            }}
            aria-pressed={isPanelOpen}
            aria-label={
              isPanelOpen
                ? t("Hide the context panel")
                : t("Show the context panel")
            }
            className={cn(
              "flex size-8 cursor-pointer items-center justify-center rounded-lg border-none p-0 text-ds-slate-800 transition-colors hover:bg-ds-slate-100",
              isPanelOpen ? "bg-ds-slate-100" : "bg-transparent"
            )}
          >
            <IconLayoutSidebarRight className="size-5" stroke={1.5} />
          </button>
        ),
      }}
      surface="default"
      className={null}
    />
  );

  return (
    <div className="flex h-full overflow-hidden font-dm-sans">
      <div className="flex min-w-0 flex-1 flex-col">
        {topNav}
        <FullscreenRegion
          enterLabel={t("Full screen")}
          exitLabel={t("Exit full screen")}
          className="min-h-0 flex-1 bg-ds-slate-50"
        >
          {(fullscreenToggle) => (
            <div className="absolute inset-0">
              {contentQuery.state === "error" ? (
                <ViewerMessage>
                  {t("Something went wrong. Please try again.")}
                </ViewerMessage>
              ) : infographic === null ? (
                <div className="flex size-full items-center justify-center">
                  <Loader expanded />
                </div>
              ) : (
                <InfographicViewer
                  infographic={infographic}
                  fullscreenToggle={fullscreenToggle}
                  onPdfPage={infographicProgress.onPdfPage}
                  onContentLoad={infographicProgress.onContentLoad}
                />
              )}
            </div>
          )}
        </FullscreenRegion>
      </div>
      {isPanelOpen ? (
        <aside className="flex w-gpt-context-panel shrink-0 flex-col py-4 pr-4">
          <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto rounded-2xl border border-ds-slate-100 bg-ds-white p-5">
            {infographic === null ? (
              <PanelSkeleton />
            ) : (
              <InfographicPanel
                infographic={infographic}
                roomId={roomId}
                room={currentRoom}
              />
            )}
          </div>
        </aside>
      ) : null}
    </div>
  );
}

function ViewerMessage({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex size-full items-center justify-center p-6 text-center">
      <p className="m-0 text-ui-body text-ds-slate-700">{children}</p>
    </div>
  );
}

/**
 * The infographic in the viewer, by its source, with one floating bar under
 * it (Figma "zoom toolbar"): an image the reader zooms, a PDF read page by
 * page, or the embedded page (a Figma Site, our own HTML), scaled as a whole
 * since its content is out of reach.
 */
function InfographicViewer({
  infographic,
  fullscreenToggle,
  onPdfPage,
  onContentLoad,
}: {
  infographic: InfographicDetail;
  fullscreenToggle: React.ReactNode;
  /** The PDF shows a page: its number and the page count (analytics). */
  onPdfPage: (page: number, count: number) => void;
  /** The image or the embedded page loaded (analytics). */
  onContentLoad: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const source = infographicSource(infographic);
  switch (source.kind) {
    case "image":
      return (
        <PanZoomImage
          key={source.url}
          src={source.url}
          alt={infographic.title}
          zoomOutLabel={t("Zoom out")}
          zoomInLabel={t("Zoom in")}
          failure={{
            message: t("This infographic can't be displayed."),
            retryLabel: t("Try again"),
          }}
          fitLabel={t("Fit to screen")}
          toolbarStart={null}
          toolbarEnd={fullscreenToggle}
          onLoad={onContentLoad}
        />
      );
    case "pdf":
      return (
        <PdfInfographic
          url={source.url}
          fullscreenToggle={fullscreenToggle}
          onPage={onPdfPage}
        />
      );
    case "embed":
      return (
        <div className="size-full p-6">
          <div className="size-full overflow-hidden rounded-xl border border-ds-slate-100 bg-ds-white">
            <ZoomableFrame
              key={source.url}
              zoomOutLabel={t("Zoom out")}
              zoomInLabel={t("Zoom in")}
              fitLabel={t("Fit to screen")}
              toolbarEnd={
                <>
                  <ViewerToolbarButton
                    label={t("Open in a new tab")}
                    isDisabled={false}
                    onClick={() => {
                      openExternalUrl(source.url);
                    }}
                  >
                    <IconExternalLink stroke={1.5} />
                  </ViewerToolbarButton>
                  {fullscreenToggle}
                </>
              }
            >
              <FigmaEmbed url={source.url} onLoad={onContentLoad} />
            </ZoomableFrame>
          </div>
        </div>
      );
    case "none":
      return (
        <ViewerMessage>
          {t("This infographic can't be displayed.")}
        </ViewerMessage>
      );
    default:
      throw new Error(source satisfies never);
  }
}

/**
 * A PDF infographic, one page at a time, as the congress documents are read:
 * the pager of the floating bar, or ← →, turns it.
 */
function PdfInfographic({
  url,
  fullscreenToggle,
  onPage,
}: {
  url: string;
  fullscreenToggle: React.ReactNode;
  onPage: (page: number, count: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [stored, setPaging] = usePdfPaging();
  const paging = stored.url === url ? stored : { url, page: 1, count: null };
  // Bounded on both sides: no page turns before the document says how many
  // it has.
  const turn = (step: -1 | 1) => {
    if (paging.count === null) return;
    const next = paging.page + step;
    if (next < 1 || next > paging.count) return;
    setPaging({ ...paging, page: next });
    onPage(next, paging.count);
  };
  const pager =
    paging.count === null ? null : (
      <>
        <ViewerToolbarButton
          label={t("Previous page")}
          isDisabled={paging.page <= 1}
          onClick={() => {
            turn(-1);
          }}
        >
          <IconChevronLeft stroke={1.5} />
        </ViewerToolbarButton>
        <span className="px-1 text-footnote font-semibold whitespace-nowrap text-ds-black">
          {t("Page {{page}} of {{count}}", {
            page: paging.page,
            count: paging.count,
          })}
        </span>
        <ViewerToolbarButton
          label={t("Next page")}
          isDisabled={paging.page >= paging.count}
          onClick={() => {
            turn(1);
          }}
        >
          <IconChevronRight stroke={1.5} />
        </ViewerToolbarButton>
      </>
    );
  return (
    <div
      className="size-full px-6 pt-8"
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          turn(-1);
        }
        if (event.key === "ArrowRight") {
          event.preventDefault();
          turn(1);
        }
      }}
    >
      {/* The page's own column is PdfViewer's scroll viewport (its parent),
          and the bar sticks to its bottom. Focusable, so a click on the page
          keeps the arrow keys here. */}
      <div
        tabIndex={-1}
        className="mx-auto size-full max-w-desktop-column overflow-auto outline-none"
      >
        <Suspense fallback={<Loader expanded />}>
          <LazyPdfViewer
            url={url}
            loadingFallback={<Loader expanded />}
            controls={{
              kind: "inline",
              zoomOutLabel: t("Zoom out"),
              zoomInLabel: t("Zoom in"),
              fitLabel: t("Fit to screen"),
              start: pager,
              end: fullscreenToggle,
            }}
            display={{
              kind: "paged",
              page: paging.page,
              fit: "width",
              onPageCount: (count) => {
                const page = Math.min(paging.page, count);
                setPaging({ url, page, count });
                onPage(page, count);
              },
            }}
          />
        </Suspense>
      </div>
    </div>
  );
}

/**
 * The panel beside the viewer: the infographic's type and date, its title,
 * its room, the actions, and the related content that the phone shows in a
 * drawer.
 */
function InfographicPanel({
  infographic,
  roomId,
  room,
}: {
  infographic: InfographicDetail;
  roomId: string | null;
  room: Api["get"]["/webapp/private/rooms/:id"]["output"]["body"] | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { articleLink, videoArticleLink, infographicLink } =
    usePageLinks(roomId);
  const { triggerShare } = useShareInfographic(
    AnalyticsItemList.INFOGRAPHIC_PAGE
  );
  const source = infographicSource(infographic);
  const externalLink = infographic.externalLink;

  const relatedQuery = useApiQuery(
    "/webapp/public/content/:id/related-content",
    { param: { id: infographic.id } }
  );
  // The articles the infographic is about first, then the related videos and
  // infographics, each once.
  const related = [
    ...infographic.associatedArticles.map((article) => ({
      id: article.id,
      label: t("Article"),
      date: formatArticleDate(article.publication_date, activeLang),
      title: article.title,
      path: articleLink(article),
    })),
    ...(relatedQuery.state === "success" ? relatedQuery.body : []).flatMap(
      (item) => {
        switch (item.content_format) {
          case "Video":
            return [
              {
                id: item.id,
                label: t("Video"),
                date: formatShortDate(item.publication_date, activeLang),
                title: item.title,
                path: videoArticleLink(item),
              },
            ];
          case "Infographic":
            return [
              {
                id: item.id,
                label: t("Infographic"),
                date: formatShortDate(item.publication_date, activeLang),
                title: item.title,
                path: infographicLink(item),
              },
            ];
          case "Nectar":
          case "Audio":
            return [];
          default:
            throw new Error(item satisfies never);
        }
      }
    ),
  ].filter(
    (item, index, all) =>
      item.id !== infographic.id &&
      all.findIndex((other) => other.id === item.id) === index
  );

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <p className="m-0 text-desktop-meta text-ds-slate-600">
          {infographic.publication_date === null
            ? t("Infographic")
            : `${t("Infographic")} · ${formatShortDate(infographic.publication_date, activeLang)}`}
        </p>
        <h1 className="m-0 text-publication-lg font-semibold text-ds-black">
          {infographic.title}
        </h1>
        {room === null ? null : <DesktopRoomSource room={room} />}
      </div>

      <div className="flex flex-col gap-2">
        {externalLink === null ? null : (
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => {
              openExternalUrl(externalLink);
            }}
          >
            {t("View original")}
          </Button>
        )}
        {currentUser === null ? null : (
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={() => {
              void triggerShare(infographic, currentUser, roomId);
            }}
          >
            {t("Share")}
          </Button>
        )}
        {source.kind === "pdf" ? (
          <a
            href={source.url}
            download
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: "outline", size: "md" })}
          >
            {t("Download")}
          </a>
        ) : null}
      </div>

      {roomId === null ? null : <RoomJoinOffer roomId={roomId} />}

      {related.length === 0 ? null : (
        <section className="flex flex-col gap-3.5 border-t border-ds-slate-100 pt-5">
          <h2 className="m-0 text-footnote font-semibold text-ds-black">
            {t("Related content")}
          </h2>
          <ul className="m-0 flex list-none flex-col gap-3.5 p-0">
            {related.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => {
                    history.push(item.path);
                  }}
                  className="flex w-full cursor-pointer flex-col gap-0.5 border-none bg-transparent p-0 text-left"
                >
                  <span className="text-desktop-meta text-ds-slate-600">
                    {item.date === ""
                      ? item.label
                      : `${item.label} · ${item.date}`}
                  </span>
                  <span className="line-clamp-2 text-footnote font-semibold text-ds-black hover:underline">
                    {item.title}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/** The panel while the infographic loads: meta, title, room, two buttons. */
function PanelSkeleton(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton className="rounded" width={140} height={14} />
      <Skeleton className="rounded" width="95%" height={24} />
      <Skeleton className="rounded" width="70%" height={24} />
      <Skeleton className="rounded" width={200} height={32} />
      <Skeleton className="rounded-lg" width="100%" height={48} />
      <Skeleton className="rounded-lg" width="100%" height={48} />
    </div>
  );
}
